import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import styles from "./styles.module.css";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../../assets/images/verticaltv/logo-trail-web01.png';
import { useTranslation } from 'react-i18next';
import { Bounce, toast, ToastContainer } from 'react-toastify';
import Loader from '../../components/Loader';
import { LANGUAGE, NAVIGATION_ROUTES, RESEND_OTP_DEBOUNCE_DELAY, RESENT_OTP_TIME, SIGN_IN_CODES, SIGNUP } from '../../constant';
import { useAuthenticateUserApi } from '../../api/useAuthApi';
import useExtraFunctions from '../../hooks/useExtraFunctions';
import { setAuthTokenInHeader } from '../../api';
import { dispatchToken } from '../../storage/tokenState';
import { useRequestOtpApi } from '../../api/useRequestOtpApi';
import { debounce } from 'lodash';
import { NavLink } from 'react-router-dom';
import { isExternalUrl, handleLinkClick } from '../../utils/externalLinkHandler';
import { CLEVERTAP_EVENTS } from '../../analytics/trackerEvents/cleverTap/events';
import { trackCleverTapEvent, setUserMobile } from '../../utils/cleverTapHelper';
import { CT_PARAMS } from '../../analytics/trackerEvents/cleverTap/parameters';
import { FbPixelEvent_CompleteRegistration } from '../../utils/FbPixel/fbPixel';
import { fireCompleteRegistrationEvent } from '../../utils/SmartScriptAppFlyer/appsFlyerEvents';
import { pushKafkaEvent } from '../../api/pushKafkaEvent';
import { pushGAEvent, GA_EVENTS } from '../../utils/gaHelper';

declare global {
  interface CredentialRequestOptions {
    otp?: {
      transport: string[];
    };
  }
  interface Credential {
    code?: string;
  }
}

// Add CleverTap profile push function
const pushCleverTapProfile = (phoneNumber: string, identity: string) => {
  if (typeof window === "undefined") return;

  const clevertap = (window as any).clevertap;
  if (!clevertap?.profile) {
    console.warn("CleverTap is not initialized yet.");
    return;
  }

  try {
    clevertap.onUserLogin.push({
      "Site": {
        "Identity": identity,
        "Phone": `+91${phoneNumber}`,
        "MSG-email": true,
        "MSG-push": true,
        "MSG-sms": true,
        "MSG-whatsapp": true,
      }
    })
    console.log(`%cCleverTap Profile Pushed for: ${phoneNumber}`, "color:green;font-weight:bold");
  } catch (error) {
    console.error("%cFailed to push CleverTap profile:", "color:red;font-weight:bold", error);
  }
};

// Update your useAutoReadSMS hook
const useAutoReadSMS = (setOtp: React.Dispatch<React.SetStateAction<string[]>>) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const popupViewTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    const handleAutoRead = async () => {
      if ('OTPCredential' in window) {
        abortControllerRef.current = new AbortController();
        
        try {
          // Track event when auto-read popup is viewed (only once)
          if (!popupViewTrackedRef.current) {
            popupViewTrackedRef.current = true;
            trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_VIEW_AUTO_READ);
          }
          
          const credential = await navigator.credentials.get({
            otp: { transport: ['sms'] },
            signal: abortControllerRef.current?.signal,
          });

          // Track event when user responds to auto-read popup (accepted)
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_AUTO_READ_RESPONDE);
          
          if (credential && 'code' in credential && credential.code) {
            const code = credential.code;
            if (code.length === 6) {
              setOtp(code.split(''));
              const form = document.querySelector('form');
              if (form) form.submit();
            }
          }
        } catch (err) {
          // Track event when user responds to auto-read popup (rejected/cancelled)
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_AUTO_READ_RESPONDE);
          console.log('WebOTP error:', err);
        }
      }

      // Fallback for Android Chrome and other browsers
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'sms' && event.data?.message) {
          const otpMatch = event.data.message.match(/\b\d{6}\b/);
          if (otpMatch) {
            const code = otpMatch[0];
            setOtp(code.split(''));
          }
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
        // Abort WebOTP request to prevent popup on other pages (payment screen, subscribed page, etc.)
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        // Remove hidden OTP input (only exists in OTP screen) to prevent Chrome from triggering WebOTP on other pages
        const hiddenOtpInput = document.getElementById('otp-autocomplete');
        if (hiddenOtpInput && hiddenOtpInput.getAttribute('autocomplete') === 'one-time-code') {
          hiddenOtpInput.remove();
        }
      };
    };

    handleAutoRead();
  }, [setOtp]);
};

const OtpScreen: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {state} = location;
  const {screenName=  SIGNUP} = state || {};
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const navigate = useNavigate();
  const { phoneNumber, action } = useParams<{ phoneNumber: string; action: string }>();
  //const { language='en' } = useTokenStore();
  const inputRefOtp = useRef<(HTMLInputElement | null)[]>([]);
  const [countdown, setCountdown] = useState<number>(RESENT_OTP_TIME);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(true);
  const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const { getPartnerId } = useExtraFunctions();
  const otpInputTrackedRef = useRef<boolean>(false); // Track if OTP input event has been fired
  const screenViewTrackedRef = useRef<boolean>(false); // Track if screen view event has been fired

  useAutoReadSMS(setOtp);

  // API hooks
  const { refetch, isLoading, error, userDetails } = useAuthenticateUserApi(
    phoneNumber as string,
    action as string,
    LANGUAGE,
    true,
    otp.join(''),
    getPartnerId(),
    screenName
  );

 
  const { refetchOTP } = useRequestOtpApi(phoneNumber as string, action as string, true, LANGUAGE);

  // Track screen view event only once when component mounts
  useEffect(() => {
    // Prevent duplicate event firing (handles React.StrictMode double-mounting)
    if (screenViewTrackedRef.current) {
      return;
    }
    screenViewTrackedRef.current = true;
    // Track CleverTap event when user lands on OTP screen
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_SCREEN_VIEW_OTP);
  }, []); // Empty dependency array - fires only once on mount

  useEffect(() => {
    inputRefOtp.current = Array(6).fill(null).map((_, i) => inputRefOtp.current[i] || null);

    setTimeout(() => {
      const firstInput = inputRefOtp.current[0];
      if (firstInput) {
        firstInput.focus();
        firstInput.click();
      }
    }, 300);

    toast.success("OTP sent successfully",{
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });
  }, []);
  

  useEffect(() => {
    if (countdown > 0 && isOtpSent) {
      const timerId = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timerId);
    }
    if (countdown === 0) setIsOtpSent(false);
  }, [countdown, isOtpSent]);

  const handleInputChange = (value: string, index: number) => {
    if (!/^[0-9]*$/.test(value)) return;
    const newOtp = [...otp];

    if (value.length > 1) {
      for (let i = 0; i < value.length && index + i < newOtp.length; i++) {
        newOtp[index + i] = value[i];
      }
    } else {
      newOtp[index] = value;
    }
    setOtp(newOtp);

    // Track OTP input event only once when complete 6-digit OTP is entered
    const isOtpComplete = newOtp.every((digit) => digit !== '');
    if (isOtpComplete && newOtp.length === 6 && !otpInputTrackedRef.current) {
      // Fire event only once when all 6 digits are entered
      otpInputTrackedRef.current = true;
      trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_OTP_INPUT);
    }

    // Only move focus forward when entering digits, not when deleting
    if (value.length > 0) {
      const nextIndex = index + (value.length > 1 ? value.length - 1 : 1);
      if (nextIndex < newOtp.length) {
        setTimeout(() => {
          inputRefOtp.current[nextIndex]?.focus();
        }, 0);
      }
    }
  };

  // Add this function to handle paste events
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault(); // Prevent the default paste behavior
    const pastedData = e.clipboardData.getData('Text'); // Get the pasted text
    handleInputChange(pastedData, index); // Call the input change handler
  };

  const handleBackspace = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index] === '' && index > 0) {
        inputRefOtp.current[index - 1]?.focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };  

  const handleResendOtp = async () => {
    try {
      setIsOtpSent(true);
      setCountdown(RESENT_OTP_TIME);
      await refetchOTP();
      toast.success(t("otpResent"));
    } catch (err) {
      toast.error(t("otpResentFailed"));
    }
  };
   const throttledResendOtp = useCallback(debounce(handleResendOtp, RESEND_OTP_DEBOUNCE_DELAY), []);

  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await refetch();
      const authResponse = response?.data;

      if (authResponse?.success && authResponse?.flag === true &&
        authResponse?.errorCode === SIGN_IN_CODES.SUCCESS_CODE) {
        // authResponse is the response object from authenticateUserApi
        // Structure: { success, flag, errorCode, signUpData: {...}, postConfig: {...} }
        const signUpData = (authResponse as any)?.signUpData || {};
        const accessToken = signUpData?.accessToken || '';
        const tokenData = signUpData || {};
        const _id = signUpData?._id || '';
        
        // Get postConfig from authResponse - check multiple possible locations
        // Note: Backend returns large postConfig with all app config, but we only need subscription data
        const postSignupConfig = (authResponse as any)?.postConfig || 
                                 (response?.data as any)?.postConfig || 
                                 (response as any)?.data?.response?.postConfig || 
                                 {};
        
        // Extract only subscription-related data from postConfig (ignore rest of the large config)
        const subscriptionData = {
          userSubscriptionData: postSignupConfig?.userSubscriptionData,
          activeSubscriptionData: postSignupConfig?.activeSubscriptionData,
          subscriptionFeatureConfig: postSignupConfig?.subscriptionFeatureConfig
        };
        
        setAuthTokenInHeader(accessToken, _id);
        
        // Convert validity from duration (milliseconds) to absolute timestamp
        // API returns validity as duration (e.g., 36060000ms = 10 hours)
        // We need to convert it to: currentTime + duration
        const tokenValidityDuration = signUpData?.validity || 0;
        const tokenDataWithTimestamp = {
          ...tokenData,
          validity: tokenValidityDuration > 0 
            ? Date.now() + tokenValidityDuration - 10 // Subtract 10ms to avoid refreshing at the last second
            : 0
        };
        
        dispatchToken(tokenDataWithTimestamp);

        // Track CleverTap event when user successfully logs in
        // Determine SOURCE based on action (signup or signin)
        const source = action === SIGNUP ? CT_PARAMS.SCREEN_SIGNUP : CT_PARAMS.SCREEN_SIGN_IN;
        // Determine USER_STATE based on isNewSignup flag from API response
        const userState = signUpData?.isNewSignup === true ? 'new_user' : 'existing_user';
        
        // Get user_id from signUpData (same pattern used across other pages)
        const userId = signUpData?._id || '';

        pushCleverTapProfile(phoneNumber as string, userId);
        
        // Track CleverTap login event with mobile number
        // Note: titleId is automatically added by trackCleverTapEvent for all WEB_EVENT_* events
        trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_LOGIN, {
          [CT_PARAMS.SOURCE]: source,
          [CT_PARAMS.USER_STATE]: userState,
          [CT_PARAMS.USER_ID]: userId,
          [CT_PARAMS.MOBILE_NUMBER]: phoneNumber // Send mobile number in login event
        });

        // Persist mobile number for all subsequent CT events
        if (phoneNumber) {
          setUserMobile(phoneNumber);
        }

        // Track Facebook Pixel events when OTP is completed and signup is successful
        FbPixelEvent_CompleteRegistration(); // CompleteRegistration event when OTP screen is completed

        // Fire AppsFlyer CompleteRegistration event after successful OTP verification
        fireCompleteRegistrationEvent({ method: 'otp_verification', page: 'otp_screen' });

        // Track GA event for OTP success
        pushGAEvent(GA_EVENTS.OTP_SUCCESS, {
          user_state: userState,
          source: source,
          user_id: userId,
          user_mobile: phoneNumber,
        });

        // Push sign_up or WEB_EVENT_LOGIN event to Kafka based on isNewSignup (non-blocking)
        // If isNewSignup is true -> 'sign_up', if false -> 'WEB_EVENT_LOGIN'
        const kafkaEventSubType = signUpData?.isNewSignup === true ? 'sign_up' : 'WEB_EVENT_LOGIN';
        // pushKafkaEvent(kafkaEventSubType, userId, phoneNumber || '', signUpData?.zupeeUserId).catch(err => {
        //   console.log(`[OtpScreen] Kafka ${kafkaEventSubType} event failed (non-critical):`, err);
        // });
        pushKafkaEvent(kafkaEventSubType, userId, phoneNumber as string, signUpData?.zupeeUserId).catch(err => {
          console.log(`[SignInForm] Kafka ${kafkaEventSubType} event failed (non-critical):`, err);
        });
        

        const userSubscriptionData = subscriptionData?.userSubscriptionData || subscriptionData?.activeSubscriptionData;
        const getCurrentTimestamp = () => Date.now();
        const currentTimestamp = getCurrentTimestamp();
        
        // Check if subscription is active based on user's requirement:
        // If userSubscriptionData exists AND isActive === true, go to subscribed page
        // Otherwise, go to subscription payment page
        const hasSubscriptionData = userSubscriptionData && typeof userSubscriptionData === 'object';
        const isActive = userSubscriptionData?.isActive === true;
        
        // If validityTill exists, also verify it's in the future
        let hasValidValidity = true; // Default to true if validityTill doesn't exist
        if (userSubscriptionData?.validityTill) {
          hasValidValidity = userSubscriptionData.validityTill > currentTimestamp;
        }
        
        // Primary check: userSubscriptionData exists and isActive is true
        // Secondary check: if validityTill exists, it should be valid
        const isSubscriptionActive = hasSubscriptionData && isActive && hasValidValidity;

        // Navigate based on source and subscription status - ensure navigation happens synchronously
        if (state?.fromLogin) {
          navigate(NAVIGATION_ROUTES.REFERRAL_APP_DOWNLOAD, { replace: true });
        } else if (isSubscriptionActive) {
          navigate(NAVIGATION_ROUTES.REFERRAL_SUBSCRIBED, { replace: true });
        } else {
          navigate(NAVIGATION_ROUTES.REFERRAL_SUBSCRIPTION_PAYMENT, { replace: true });
        }
      } else if (authResponse?.success && authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
        if (authResponse?.errorCode === SIGN_IN_CODES.EXISTING_USER_ERROR_CODE) {
          setTimeout(() => {
            setIsExistingUser(true);
            setOtp(Array(6).fill(''));
            // toast.error(authResponse?.error || t("invalidOtp"));
          }, 0);
          return;
        }
        setTimeout(() => { 
          setOtp(Array(6).fill('')); 
          toast.error(authResponse.error, { toastId: authResponse.error });
        }, 0);
      }
      
    } catch (error) {
      
      setTimeout(() => {
        setOtp(Array(6).fill(''));
        toast.error(t("errorOccurred"));
      }, 0);
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== '');

  // Auto-submit disabled - user must click "Verify OTP" button manually
  // useEffect(() => {
  //   if (isOtpComplete) {
  //     console.log("isOtpComplete", isOtpComplete);
  //     setTimeout(() => handleSubmit(new Event('submit') as unknown as React.FormEvent), 250);
  //   }
  // }, [isOtpComplete]);


  useEffect(() => {
    const handleSms = (event: MessageEvent) => {
      if (event.data && event.data.type === 'sms' && event.data.message) {
        const otpMatch = event.data.message.match(/(\d{6})/);
        if (otpMatch) {
          setOtp(otpMatch[1].split(''));
        }
      }
    };

    window.addEventListener('message', handleSms);

    return () => {
      window.removeEventListener('message', handleSms);
      setIsExistingUser(false);
    };
  }, []);

  if (isLoading) return <Loader />;

  // Edit icon SVG
  const EditIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 20 20" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ pointerEvents: 'none' }}
    >
      <path d="M11.05 3.00002L4.20835 10.2417C4.00002 10.4667 3.83335 10.9 3.78335 11.1917L3.50835 12.725C3.40002 13.35 3.98335 13.9334 4.60835 13.825L6.14168 13.55C6.43335 13.5 6.86668 13.3334 7.09168 13.125L13.9334 5.88335C14.6334 5.18335 14.9834 4.33335 13.9334 3.28335C12.8834 2.23335 12.0334 2.57502 11.3334 3.27502L11.05 3.00002Z" stroke="#8D93A8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.90833 4.20837C10.2667 5.95837 11.7083 7.3917 13.4667 7.75003" stroke="#8D93A8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2.5 18.3334H17.5" stroke="#8D93A8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className={clsx(styles.otpContainer, "authPageContainer")}>
      <div className={styles.backgroundGlow}>
      <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="360" 
                    height="684" 
                    viewBox="0 0 360 684" 
                    fill="none"
                    className={styles.backgroundSvg}
                >
                    <g filter="url(#filter0_fn_527_9064)">
                        <circle cx="180" cy="92" r="158" fill="#4F5568"/>
                    </g>
                    <defs>
                        <filter 
                            id="filter0_fn_527_9064" 
                            x="-412" 
                            y="-500" 
                            width="1184" 
                            height="1184" 
                            filterUnits="userSpaceOnUse" 
                            colorInterpolationFilters="sRGB"
                        >
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                            <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_527_9064"/>
                            <feTurbulence 
                                type="fractalNoise" 
                                baseFrequency="0.5 0.5" 
                                stitchTiles="stitch" 
                                numOctaves="3" 
                                result="noise" 
                                seed="2135"
                            />
                            <feComponentTransfer in="noise" result="coloredNoise1">
                                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                                <feFuncA 
                                    type="discrete" 
                                    tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
                                />
                            </feComponentTransfer>
                            <feComposite 
                                operator="in" 
                                in2="effect1_foregroundBlur_527_9064" 
                                in="coloredNoise1" 
                                result="noise1Clipped"
                            />
                            <feComponentTransfer in="noise1Clipped" result="color1">
                                <feFuncA type="table" tableValues="0 0.4"/>
                            </feComponentTransfer>
                            <feMerge result="effect2_noise_527_9064">
                                <feMergeNode in="effect1_foregroundBlur_527_9064"/>
                                <feMergeNode in="color1"/>
                            </feMerge>
                        </filter>
                    </defs>
                </svg>
      </div>
      <ToastContainer position="bottom-center"/>
      {!isExistingUser && (
        <div className={clsx(styles.contentBox, "authContentBox")}>
          {/* Logo Section */}
          <div className={styles.logoContainer}>
            <img src={logo} alt="Vertical TV by Zupee" className={styles.logo} />
          </div>

          {/* Title and Phone Number Section */}
          <div className={styles.titleSection}>
            <h1 className={clsx(styles.pageHeading, "fontSizeMd", "fontWeightBold")}>
              Enter OTP
            </h1>
            <div className={styles.phoneNumberSection}>
              <p className={clsx(styles.otpSentLabel, "fontSizeSm14")}>
                OTP sent to <span className={styles.otpNo}>{phoneNumber}</span>
              </p>
              <div 
                className={styles.editIconContainer}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(NAVIGATION_ROUTES.REFERRAL_SIGNUP, { state });
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <EditIcon />
              </div>
            </div>
          </div>

          {/* OTP Input and Resend Section */}
          <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
            {/* Hidden input for OTP autofill - browser will use this for auto-read OTP */}
            {/* This input is required for mobile browsers to detect and auto-fill OTP from SMS */}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              name="otp"
              id="otp-autocomplete"
              style={{ 
                position: 'absolute', 
                opacity: 0, 
                pointerEvents: 'none', 
                width: 0, 
                height: 0,
                left: '-9999px'
              }}
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const code = e.target.value.replace(/\D/g, '').slice(0, 6);
                if (code.length === 6) {
                  setOtp(code.split(''));
                  // Focus the last input after auto-fill
                  setTimeout(() => {
                    if (inputRefOtp.current[5]) {
                      inputRefOtp.current[5].focus();
                    }
                  }, 100);
                }
              }}
            />
            <div className={clsx(styles.otpBlock, '')}>
              {otp.map((digit, index) => (
                <div key={index} className={styles.otpInputWrapper}>
                  {focusedIndex === index && !digit && (
                    <div className={styles.otpCursor}>|</div>
                  )}
                  <input
                    className={clsx(styles.otpInput, (digit || focusedIndex === index) && styles.active, focusedIndex === index && !digit && styles.hideCaret)}
                    type="number"
                    maxLength={1}
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleInputChange(e.target.value, index)}
                    onKeyDown={(e) => handleBackspace(e, index)}
                    onPaste={(e) => handlePaste(e, index)}
                    onFocus={() => setFocusedIndex(index)}
                    onBlur={() => setFocusedIndex(-1)}
                    ref={(el) => (inputRefOtp.current[index] = el)}
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    pattern="\d*"
                    readOnly={false}
                    autoFocus={index === 0}
                  />
                </div>
              ))}
            </div>
            <p
              className={clsx(
                styles.resendOtp,
                isOtpSent && styles.disabled
              )}
              onClick={isOtpSent ? undefined : throttledResendOtp}
            >
              {isOtpSent
                ? `${t("retryIn")} ${countdown}${t("sec")}`
                : t("resendOTP")}
            </p>
            <button
              type="submit"
              disabled={!isOtpComplete}
              className={clsx(styles.otpBtn, "btn primaryBtn")}
            >
              Verify OTP
            </button>
            <div className={clsx(styles.declarationBlock, 'declarationBlockOtp')}>
              <label className={clsx(styles.declarationLabel, "fontSizeXs fontWeightRegular declarationLabelOtp")}>
                <span>By entering your number, you're agreeing to our</span>
                {isExternalUrl(NAVIGATION_ROUTES.REFERRAL_TNC) ? (
                  <a
                    href={NAVIGATION_ROUTES.REFERRAL_TNC}
                    onClick={(e) => handleLinkClick(NAVIGATION_ROUTES.REFERRAL_TNC, e)}
                    className={clsx(styles.linkText, "fontWeightBold", 'fontSizeSm12')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>
                ) : (
                  <NavLink
                    to={NAVIGATION_ROUTES.REFERRAL_TNC}
                    state={{ url: NAVIGATION_ROUTES.REFERRAL_TNC }}
                    className={clsx(styles.linkText, "fontWeightBold", 'fontSizeSm12')}
                  >
                    Terms & Conditions
                  </NavLink>
                )}
              </label>
            </div>
          </form>

        </div>
      )}
      {/* <ExistingZupeeUserModal
        isExistingUser={isExistingUser}
        setIsExistingUser={setIsExistingUser}
      /> */}
    </div>
  );
};

export default OtpScreen;